import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import axios from 'axios';
import { BASE_URL, SEASON_API_PATH } from 'constants/api';
import { getApiServiceName, SERVICE_OPTIONS } from 'constants/serviceDistribution';
import { contentAssetService } from 'content/library/service/contentAssetService';
import { seasonContentService } from 'content/season/services/seasonContentService';
import { SeasonContentInterface } from 'content/season/types/seasonContentTypes';
import { seasonService, seriesService } from 'content/service';
import { contentService } from 'content/service/contentService';
import React, { useState } from 'react';
import { videoService } from 'service/api/videoService';
import { SeasonData } from 'type/seasonType';
import { slugify } from 'utils/slugify';
import { validateVideoAssetPrimary } from '../validators/contentValidators';
import {
  checkDistributionReflection,
  checkDistributionStatus,
  checkServiceAutoDistributions,
  validateContents,
  validatePosters,
} from '../validators/seasonValidators';

const { Title, Text } = Typography;

interface DebugResult {
  step: string;
  status: 'success' | 'error' | 'warning' | 'info';
  message: string;
  details?: any;
}

const Debug: React.FC = () => {
  const [seasonId, setSeasonId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [season, setSeason] = useState<SeasonData | null>(null);
  const [_contents, setContents] = useState<SeasonContentInterface[]>([]);
  const [debugResults, setDebugResults] = useState<DebugResult[]>([]);
  const [editingSlug, setEditingSlug] = useState<Record<number, string>>({});
  const [savingSlug, setSavingSlug] = useState<Record<number, boolean>>({});

  const handleOpenDistributionAdmin = (programId: number, serviceName: string) => {
    const hostMap: Record<string, string> = {
      'odc-staging': 'https://admin.staging2.odkmedia.net/odc',
      odc: 'https://admin.odkmedia.net/odc',
      'odk-staging': 'https://admin.staging2.odkmedia.net/odk',
      odk: 'https://admin.odkmedia.net/odk',
      'amasian-staging': 'https://amasian-admin.staging2.odkmedia.net',
      amasian: 'https://amasian-admin.odkmedia.net',
    };
    if (serviceName === 'amasian' || serviceName === 'amasian-staging') {
      const url = `${hostMap[serviceName]}/season/edit/${programId}`;
      window.open(url, '_blank');
    } else if (serviceName === 'odc' || serviceName === 'odc-staging') {
      const url = `${hostMap[serviceName]}/content/program/${programId}`;
      window.open(url, '_blank');
    } else if (serviceName === 'odk' || serviceName === 'odk-staging') {
      const url = `${hostMap[serviceName]}/content/program/${programId}`;
      window.open(url, '_blank');
    } else {
      const url = `${hostMap[serviceName]}/content/program/${programId}`;
      window.open(url, '_blank');
    }
  };

  const handleOpenContentDistributionAdmin = (episodeId: number, serviceName: string) => {
    const hostMap: Record<string, string> = {
      'odc-staging': 'https://admin.staging2.odkmedia.net/odc',
      odc: 'https://admin.odkmedia.net/odc',
      'odk-staging': 'https://admin.staging2.odkmedia.net/odk',
      odk: 'https://admin.odkmedia.net/odk',
      'amasian-staging': 'https://amasian-admin.staging2.odkmedia.net',
      amasian: 'https://amasian-admin.odkmedia.net',
    };
    if (serviceName === 'amasian' || serviceName === 'amasian-staging') {
      const url = `${hostMap[serviceName]}/video/detail/${episodeId}`;
      window.open(url, '_blank');
    } else if (serviceName === 'odc' || serviceName === 'odc-staging') {
      const url = `${hostMap[serviceName]}/content/episode/${episodeId}`;
      window.open(url, '_blank');
    } else if (serviceName === 'odk' || serviceName === 'odk-staging') {
      const url = `${hostMap[serviceName]}/content/episode/${episodeId}`;
      window.open(url, '_blank');
    } else {
      const url = `${hostMap[serviceName]}/content/episode/${episodeId}`;
      window.open(url, '_blank');
    }
  };

  const handleUpdateSlug = async (contentId: number, newSlug: string) => {
    try {
      setSavingSlug(prev => ({ ...prev, [contentId]: true }));
      await contentService.update(contentId, { slug: slugify(newSlug) });
      message.success('Slug updated successfully.');
      // Reload debug results
      if (season) {
        handleDebug();
      }
    } catch (error: any) {
      Modal.error({ title: 'Error', content: `Failed to update slug: ${error.message || 'Unknown error'}`, okText: 'Confirm' });
    } finally {
      setSavingSlug(prev => ({ ...prev, [contentId]: false }));
    }
  };

  const [reDistributing, setReDistributing] = useState<Record<string, boolean>>({});
  const [contentDebugResults, setContentDebugResults] = useState<Record<number, any>>({});
  const [contentDebugLoading, setContentDebugLoading] = useState<Record<number, boolean>>({});
  const [regeneratingManifests, setRegeneratingManifests] = useState<Record<string, boolean>>({});
  const [transcodingVideo, setTranscodingVideo] = useState<Record<number, boolean>>({});

  const handleReDistribution = async (
    seasonId: number,
    serviceName: string,
    apiServiceName: string
  ) => {
    try {
      setReDistributing(prev => ({ ...prev, [serviceName]: true }));

      // First check if an existing distribution exists
      const seasonData = await seasonService.fetchSeasonWithI18n(seasonId);
      const distributions = seasonData.distributions || [];
      const existingDist = distributions.find(
        (d: any) => d.content_consumer_name === apiServiceName
      );

      if (existingDist && existingDist.id) {
        // Update if existing distribution found
        await axios.patch(
          `${BASE_URL}${SEASON_API_PATH}${seasonId}/distributions/${existingDist.id}/`,
          { status: 'BEFORE_SYNC' }
        );
      } else {
        // Create if not found
        await axios.post(`${BASE_URL}${SEASON_API_PATH}${seasonId}/distributions/`, {
          content_consumer_name: apiServiceName,
          status: 'BEFORE_SYNC',
        });
      }

      message.success(`${serviceName} Distribution changed to BEFORE_SYNC.`);
      // Reload debug results
      if (season) {
        handleDebug();
      }
    } catch (error: any) {
      Modal.error({ title: 'Error', content: `${serviceName} Re Distribution failed: ${error.response?.data?.message || error.message || 'Unknown error'}`, okText: 'Confirm' });
    } finally {
      setReDistributing(prev => ({ ...prev, [serviceName]: false }));
    }
  };

  const handleReDistributionContent = async (
    contentId: number,
    serviceName: string,
    apiServiceName: string
  ) => {
    try {
      setReDistributing(prev => ({ ...prev, [serviceName]: true }));
      await contentService.upsertDistribution(contentId, apiServiceName, 'BEFORE_SYNC');
      message.success(`${serviceName} Distribution changed to BEFORE_SYNC.`);
      // Reload debug results
      if (contentId) {
        handleDebugContent(contentId);
      }
    } catch (error: any) {
      Modal.error({ title: 'Error', content: `${serviceName} Re Distribution failed: ${error.response?.data?.message || error.message || 'Unknown error'}`, okText: 'Confirm' });
    } finally {
      setReDistributing(prev => ({ ...prev, [serviceName]: false }));
    }
  };

  const handleBulkReDistributionContents = async (
    seasonId: number,
    contents: SeasonContentInterface[],
    serviceName: string
  ) => {
    try {
      const key = `bulk-${serviceName}`;
      setReDistributing(prev => ({ ...prev, [key]: true }));

      // Upsert distribution for each content
      const updatePromises = contents.map(async content => {
        try {
          await contentService.upsertDistribution(content.id, serviceName, 'BEFORE_SYNC');
        } catch (error) {
          console.error(`Failed to upsert distribution for content ${content.id}:`, error);
        }
      });

      await Promise.all(updatePromises);
      message.success(
        `All ${contents.length} contents' ${serviceName} distributions changed to BEFORE_SYNC.`
      );

      // Reload debug results
      if (seasonId) {
        handleDebug();
      }
    } catch (error: any) {
      Modal.error({ title: 'Error', content: `Bulk Re Distribution failed: ${error.response?.data?.message || error.message || 'Unknown error'}`, okText: 'Confirm' });
    } finally {
      const key = `bulk-${serviceName}`;
      setReDistributing(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAddSubtitle = async (contentId: number, serviceName: string) => {
    try {
      const result = contentDebugResults[contentId];
      if (!result || !result.contentVideoAssets) {
        Modal.error({ title: 'Error', content: 'Content video assets not found.', okText: 'Confirm' });
        return;
      }

      // Find primary asset for this service
      const primaryAsset = result.contentVideoAssets.find(
        (asset: any) =>
          asset.service_name === serviceName &&
          ((asset as any).is_primary === true ||
            asset.primary === true ||
            asset.is_default === true)
      );

      if (!primaryAsset || !primaryAsset.id) {
        Modal.error({ title: 'Error', content: `Primary asset not found for service: ${serviceName}`, okText: 'Confirm' });
        return;
      }

      const key = `${contentId}-${serviceName}`;
      setRegeneratingManifests(prev => ({ ...prev, [key]: true }));

      await contentAssetService.addSubtitle(primaryAsset.id);
      message.success(`Stream manifests regeneration request sent for ${serviceName}.`);
    } catch (error: any) {
      Modal.error({ title: 'Error', content: `Failed to regenerate stream manifests: ${error.response?.data?.message || error.message || 'Unknown error'}`, okText: 'Confirm' });
    } finally {
      const key = `${contentId}-${serviceName}`;
      setRegeneratingManifests(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleVideoTranscode = async (primaryVideoId: number) => {
    try {
      setTranscodingVideo(prev => ({ ...prev, [primaryVideoId]: true }));

      await videoService.videoAssetTranscode(primaryVideoId);
      message.success(`Video transcoding request sent for video ID: ${primaryVideoId}.`);
    } catch (error: any) {
      Modal.error({ title: 'Error', content: `Failed to start video transcoding: ${error.response?.data?.message || error.message || 'Unknown error'}`, okText: 'Confirm' });
    } finally {
      setTranscodingVideo(prev => ({ ...prev, [primaryVideoId]: false }));
    }
  };

  const handleDebugContent = async (contentId: number) => {
    try {
      setContentDebugLoading(prev => ({ ...prev, [contentId]: true }));

      // 1. Check content_video_asset
      const contentVideoAssets = await contentAssetService.fetchContentVideoAssets(contentId);

      // 1-3, 1-4, 1-5. Fetch content information
      const contentData = await contentService.fetchById(contentId);

      // Get season and series data for service_auto_distribution_inherit check
      let seasonData: any = null;
      let seriesData: any = null;

      if ((contentData as any).service_auto_distribution_inherit) {
        const seasonId = (contentData as any).season_id || (contentData as any).season;
        if (seasonId) {
          try {
            seasonData = await seasonService.fetchSeasonWithI18n(Number(seasonId));
            if (seasonData.service_auto_distribution_inherit && seasonData.series) {
              try {
                seriesData = await seriesService.fetchById(Number(seasonData.series));
              } catch (error) {
                console.error('Failed to fetch series data:', error);
              }
            }
          } catch (error) {
            console.error('Failed to fetch season data:', error);
          }
        }
      }

      // Validate all video asset checks (1-1 through 1-7)
      const videoAssetValidation = await validateVideoAssetPrimary(
        contentVideoAssets,
        contentData,
        seasonData,
        seriesData
      );

      const result = {
        contentId,
        contentVideoAssets, // Store for finding asset IDs by service
        contentData, // Store for distribution reflection check
        videoAsset: {
          hasVideoAsset: videoAssetValidation.hasVideoAsset,
          totalCount: videoAssetValidation.totalCount,
          primaryCount: videoAssetValidation.primaryCount,
          isValidPrimaryCount: videoAssetValidation.isValidPrimaryCount,
          primaryVideoIdCount: videoAssetValidation.primaryVideoIdCount,
          primaryAsset: videoAssetValidation.primaryAsset,
        },
        thumbnailAndPoster: videoAssetValidation.thumbnailAndPoster,
        streamManifests: videoAssetValidation.streamManifests,
        videoStreamManifests: videoAssetValidation.videoStreamManifests,
        serviceAutoDistributions: videoAssetValidation.serviceAutoDistributions,
        distributions: videoAssetValidation.distributions,
        distributionSets: videoAssetValidation.distributionSets,
        distributionStatus: videoAssetValidation.distributionStatus,
        distributionReflection: videoAssetValidation.distributionReflection,
        validationIssues: videoAssetValidation.validationIssues,
        hasIssues:
          videoAssetValidation.validationIssues.length > 0 ||
          !videoAssetValidation.hasVideoAsset ||
          !videoAssetValidation.isValidPrimaryCount,
      };

      setContentDebugResults(prev => ({ ...prev, [contentId]: result }));
    } catch (error: any) {
      Modal.error({ title: 'Error', content: `Content debugging failed: ${error.message || 'Unknown error'}`, okText: 'Confirm' });
      console.error('Content debug error:', error);
    } finally {
      setContentDebugLoading(prev => ({ ...prev, [contentId]: false }));
    }
  };

  const handleDebug = async () => {
    if (!seasonId || isNaN(Number(seasonId))) {
      Modal.error({ title: 'Error', content: 'Please enter a valid Season ID.', okText: 'Confirm' });
      return;
    }

    setLoading(true);
    setDebugResults([]);
    const results: DebugResult[] = [];

    try {
      // Fetch Season data
      const seasonData = await seasonService.fetchSeasonWithI18n(Number(seasonId));
      setSeason(seasonData);

      // Fetch Contents
      const contentsData = await seasonContentService.getSeasonContents(Number(seasonId));
      setContents(contentsData.contents);

      const posterResult = validatePosters(seasonData);
      results.push({
        step: '1. Poster Data Check',
        status:
          posterResult.duplicates && posterResult.duplicates.length > 0 ? 'warning' : 'success',
        message:
          posterResult.duplicates && posterResult.duplicates.length > 0
            ? posterResult.message
            : 'No duplicate posters.',
        details: posterResult,
      });

      const serviceAutoDistributions = await checkServiceAutoDistributions(
        seasonData,
        seriesService
      );
      const distributionStatus = checkDistributionStatus(seasonData);
      const distributionSets = seasonData.distribution_sets || [];

      // Check reflection status for each service (excluding Amasian)
      const reflectionResult = checkDistributionReflection(distributionStatus, seasonData);
      const serviceStatusMessages = reflectionResult.serviceStatusMessages;

      const contentsResult = validateContents(contentsData.contents, seasonData);
      results.push({
        step: '2. Season Contents Check',
        status: contentsResult.status,
        message: contentsResult.message,
        details: contentsResult,
      });

      const statusMessage =
        serviceStatusMessages.length > 0
          ? serviceStatusMessages.join(' / ')
          : 'Service Auto Distribution and Distribution Status Check';

      results.push({
        step: '3. Service Distribution Check',
        status: 'info',
        message: statusMessage,
        details: {
          serviceAutoDistributions,
          distributionStatus,
          distributionSets,
          season: seasonData,
        },
      });
      setDebugResults(results);
    } catch (error: any) {
      Modal.error({ title: 'Error', content: `An error occurred during debugging: ${error.message || 'Unknown error'}`, okText: 'Confirm' });
      results.push({
        step: 'Error',
        status: 'error',
        message: error.message || 'An unknown error occurred.',
      });
      setDebugResults(results);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return '#52c41a';
      case 'error':
        return '#ff4d4f';
      case 'warning':
        return '#faad14';
      default:
        return '#1890ff';
    }
  };

  return (
    <div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Title level={4}>Contents Debugging</Title>
            <Text type="secondary">Enter a Season ID to view debugging information.</Text>
          </div>

          <Space>
            <Input
              placeholder="Enter Season ID"
              value={seasonId}
              onChange={e => setSeasonId(e.target.value)}
              style={{ width: 200 }}
              onPressEnter={handleDebug}
            />
            <Button type="primary" onClick={handleDebug} loading={loading}>
              Start Debugging
            </Button>
          </Space>
        </Space>
      </Card>

      {loading && (
        <Card style={{ marginTop: 16 }}>
          <Spin size="large" />
        </Card>
      )}

      {debugResults.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <Title level={4}>Debug Results</Title>

          {season && (
            <Card type="inner" title="Season Information" style={{ marginBottom: 16 }} size="small">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="ID">{season.id}</Descriptions.Item>
                <Descriptions.Item label="Title">
                  {season.title || season.i18n_title?.ko || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Slug">{season.slug}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={season.status === 'published' ? 'green' : 'orange'}>
                    {season.status}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {debugResults.map((result, index) => (
            <Card
              key={index}
              type="inner"
              title={
                <Space>
                  {getStatusIcon(result.status)}
                  <span>{result.step}</span>
                </Space>
              }
              style={{
                marginBottom: 16,
                borderLeft: `4px solid ${getStatusColor(result.status)}`,
              }}
            >
              <Alert
                message={result.message}
                type={
                  result.status === 'success'
                    ? 'success'
                    : result.status === 'error'
                      ? 'error'
                      : result.status === 'warning'
                        ? 'warning'
                        : 'info'
                }
                style={{ marginBottom: 16 }}
              />

              {result.details && (
                <div>
                  {result.details.serviceAutoDistributions && (
                    <div>
                      {SERVICE_OPTIONS.map(service => {
                        const serviceAutoDist =
                          result.details.serviceAutoDistributions?.[service.name];
                        const distributionStatus =
                          result.details.distributionStatus?.[service.name];
                        const hasServiceAutoDist = serviceAutoDist?.enabled;
                        const hasDistribution = distributionStatus?.enabled;

                        // Check Distribution Set
                        const distributionSets = result.details.distributionSets || [];
                        const serviceDistributionSets = distributionSets.filter(
                          (ds: any) =>
                            ds.service_name === service.apiName || ds.service_name === service.name
                        );
                        const hasDistributionSets = serviceDistributionSets.length > 0;

                        // Amasian check
                        const isAmasian = service.apiName === 'amasian';

                        return (
                          <Card
                            key={service.name}
                            size="small"
                            title={
                              <Space>
                                <span>{service.name}</span>
                                {hasServiceAutoDist && !isAmasian && (
                                  <Button
                                    size="small"
                                    type="primary"
                                    loading={reDistributing[service.name]}
                                    onClick={() => {
                                      const apiServiceName = getApiServiceName(service.name);
                                      handleReDistribution(
                                        Number(seasonId),
                                        service.name,
                                        apiServiceName
                                      );
                                    }}
                                  >
                                    Re Distribution
                                  </Button>
                                )}
                              </Space>
                            }
                            style={{ marginBottom: 16 }}
                          >
                            <Row gutter={16}>
                              <Col span={12}>
                                <Card type="inner" title="Service Auto Distribution" size="small">
                                  {hasServiceAutoDist ? (
                                    <Space
                                      direction="vertical"
                                      style={{ width: '100%' }}
                                      size="small"
                                    >
                                      <div>
                                        <Text
                                          strong
                                          style={{ display: 'inline-block', width: 120 }}
                                        >
                                          Status:
                                        </Text>
                                        <Tag color="green">Enabled</Tag>
                                      </div>
                                      <div>
                                        <Text
                                          strong
                                          style={{ display: 'inline-block', width: 120 }}
                                        >
                                          Service Name:
                                        </Text>
                                        <Text>{serviceAutoDist.service_name}</Text>
                                      </div>
                                      <div>
                                        <Text
                                          strong
                                          style={{ display: 'inline-block', width: 120 }}
                                        >
                                          Modified:
                                        </Text>
                                        <Text>{result.details.season?.modified}</Text>
                                      </div>
                                      {serviceAutoDist.subtitle_langs &&
                                        serviceAutoDist.subtitle_langs.length > 0 && (
                                          <div>
                                            <Text
                                              strong
                                              style={{ display: 'inline-block', width: 120 }}
                                            >
                                              Subtitles:
                                            </Text>
                                            <Space wrap>
                                              {serviceAutoDist.subtitle_langs.map(
                                                (lang: string) => (
                                                  <Tag key={lang} color="blue">
                                                    {lang}
                                                  </Tag>
                                                )
                                              )}
                                            </Space>
                                          </div>
                                        )}
                                    </Space>
                                  ) : (
                                    <Tag color="red">Disabled</Tag>
                                  )}
                                </Card>
                              </Col>
                              <Col span={12}>
                                <Card type="inner" title="Distribution Status" size="small">
                                  {isAmasian ? (
                                    <Tag color="blue">
                                      Distribution does not exist at Season level
                                    </Tag>
                                  ) : hasDistribution ? (
                                    <Space
                                      direction="vertical"
                                      style={{ width: '100%' }}
                                      size="small"
                                    >
                                      <div>
                                        <Text
                                          strong
                                          style={{ display: 'inline-block', width: 120 }}
                                        >
                                          Status:
                                        </Text>
                                        <Tag
                                          color={
                                            distributionStatus.status === 'COMPLETED'
                                              ? 'green'
                                              : distributionStatus.status === 'PENDING'
                                                ? 'orange'
                                                : 'default'
                                          }
                                        >
                                          {distributionStatus.status}
                                        </Tag>
                                      </div>
                                      <div>
                                        <Text
                                          strong
                                          style={{ display: 'inline-block', width: 150 }}
                                        >
                                          Content Consumer:
                                        </Text>
                                        <Text>{distributionStatus.content_consumer_name}</Text>
                                      </div>
                                      <div>
                                        <Text
                                          strong
                                          style={{ display: 'inline-block', width: 120 }}
                                        >
                                          Modified:
                                        </Text>
                                        <Text>{distributionStatus.modified}</Text>
                                      </div>
                                    </Space>
                                  ) : (
                                    <Tag color="red">Disabled</Tag>
                                  )}
                                </Card>
                              </Col>
                            </Row>
                            {/* Reflection status display (excluding Amasian) */}
                            {!isAmasian &&
                              hasDistribution &&
                              distributionStatus.modified &&
                              result.details.season?.modified && (
                                <div style={{ marginTop: 16 }}>
                                  {distributionStatus.status !== 'COMPLETED' ||
                                  new Date(distributionStatus.modified) <
                                    new Date(result.details.season.modified) ? (
                                    <Text>
                                      <Tag color="orange" icon={<WarningOutlined />}>
                                        Newly modified content has not been reflected
                                      </Tag>
                                    </Text>
                                  ) : (
                                    <Text>
                                      <Tag color="green" icon={<CheckCircleOutlined />}>
                                        Newly modified content has been reflected
                                      </Tag>
                                    </Text>
                                  )}
                                </div>
                              )}
                            {/* Distribution Set information */}
                            {hasServiceAutoDist && hasDistributionSets && (
                              <div style={{ marginTop: 16 }}>
                                <Card type="inner" title="Distribution Set" size="small">
                                  <Space
                                    direction="vertical"
                                    style={{ width: '100%' }}
                                    size="small"
                                  >
                                    <div>
                                      <Text strong style={{ display: 'inline-block', width: 150 }}>
                                        Distribution Sets:
                                      </Text>
                                      <Space
                                        direction="vertical"
                                        size="small"
                                        style={{ marginTop: 8 }}
                                      >
                                        {serviceDistributionSets.map((ds: any, idx: number) => (
                                          <div key={idx}>
                                            <Space>
                                              <Tag>
                                                {ds.service_name} - Program ID: {ds.program_id}
                                              </Tag>
                                              <Button
                                                type="link"
                                                onClick={() =>
                                                  handleOpenDistributionAdmin(
                                                    ds.program_id,
                                                    ds.service_name
                                                  )
                                                }
                                              >
                                                {'Link to'}
                                              </Button>
                                            </Space>
                                          </div>
                                        ))}
                                      </Space>
                                    </div>
                                  </Space>
                                </Card>
                              </div>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  )}
                  {result.step.includes('Poster') && (
                    <div>
                      {result.details.duplicates && result.details.duplicates.length > 0 ? (
                        <Card
                          size="small"
                          title="Duplicate Posters"
                          style={{ borderColor: '#faad14', borderWidth: 2 }}
                        >
                          <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            {result.details.duplicates.map((dup: any, index: number) => (
                              <Card
                                key={index}
                                type="inner"
                                size="small"
                                title={
                                  <Space>
                                    <Tag color="orange">
                                      {dup.language} / {dup.orientation}
                                    </Tag>
                                    <Text type="secondary">({dup.count} items)</Text>
                                  </Space>
                                }
                              >
                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                  {dup.posters.map((poster: any, pIndex: number) => (
                                    <div
                                      key={pIndex}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '4px 0',
                                      }}
                                    >
                                      <div>
                                        <img
                                          src={poster.url}
                                          alt={poster.language}
                                          width={100}
                                          height={100}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: 4,
                                        }}
                                      >
                                        <Text type="secondary">file url: {poster.url}</Text>
                                        <Text type="secondary">ID: {poster.id}</Text>
                                      </div>
                                      <Popconfirm
                                        title="Are you sure you want to delete this poster?"
                                        onConfirm={async () => {
                                          try {
                                            await seasonService.removePoster(Number(seasonId), [
                                              poster,
                                            ]);
                                            message.success('Poster deleted successfully.');
                                            handleDebug();
                                          } catch (error: any) {
                                            Modal.error({ title: 'Error', content: `Failed to delete poster: ${error.message || 'Unknown error'}`, okText: 'Confirm' });
                                          }
                                        }}
                                        okText="Delete"
                                        cancelText="Cancel"
                                      >
                                        <Button
                                          type="text"
                                          danger
                                          size="large"
                                          style={{
                                            borderRadius: 10,
                                            border: '1px solid #000',
                                          }}
                                          icon={<CloseCircleOutlined />}
                                        />
                                      </Popconfirm>
                                    </div>
                                  ))}
                                </Space>
                              </Card>
                            ))}
                          </Space>
                        </Card>
                      ) : (
                        <div></div>
                      )}
                    </div>
                  )}
                  {result.step.includes('Contents') && (
                    <div>
                      <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={12}>
                          <Statistic
                            title="Total Contents Count"
                            value={result.details.count}
                            valueStyle={{ color: result.details.count > 0 ? '#3f8600' : '#cf1322' }}
                          />
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="Content Type Count"
                            value={Object.keys(result.details.contentTypeCounts || {}).length}
                          />
                        </Col>
                      </Row>
                      {result.details.contentTypeCounts && (
                        <Card size="small" title="Content Type Counts" style={{ marginBottom: 8 }}>
                          <Space>
                            {Object.entries(result.details.contentTypeCounts).map(
                              ([type, count]: [string, any]) => (
                                <Tag key={type}>
                                  {type}: {count}
                                </Tag>
                              )
                            )}
                          </Space>
                        </Card>
                      )}

                      {/* Slug check results */}
                      {result.details.slugErrors && result.details.slugErrors.length > 0 && (
                        <Collapse
                          items={[
                            {
                              key: 'slug-errors',
                              label: (
                                <span style={{ fontWeight: 'bold', color: '#faad14' }}>
                                  Slug Mismatch ({result.details.slugErrors.length} items)
                                </span>
                              ),
                              children: (
                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                  {result.details.slugErrors.map((error: any) => (
                                    <Card
                                      key={error.id}
                                      size="small"
                                      style={{ backgroundColor: '#fffbe6' }}
                                    >
                                      <Space
                                        direction="vertical"
                                        style={{ width: '100%' }}
                                        size="small"
                                      >
                                        <div>
                                          <Text strong>Content ID: {error.id}</Text>
                                          <br />
                                          <Text type="secondary">
                                            Episode Order: {error.episode_order}
                                          </Text>
                                        </div>
                                        <div>
                                          <Text type="secondary">
                                            Episode Part: {error.episode_part}
                                          </Text>
                                        </div>
                                        <div>
                                          <Text>Current: </Text>
                                          <Text code>{error.current}</Text>
                                        </div>
                                        <div>
                                          <Text type="danger">Expected: </Text>
                                          <Text code type="danger">
                                            {error.expected}
                                          </Text>
                                        </div>
                                        <div>
                                          <Space>
                                            <Input
                                              placeholder="Enter new slug"
                                              value={editingSlug[error.id] || error.current}
                                              onChange={e =>
                                                setEditingSlug(prev => ({
                                                  ...prev,
                                                  [error.id]: e.target.value,
                                                }))
                                              }
                                              style={{ width: 300 }}
                                            />
                                            <Button
                                              type="primary"
                                              size="small"
                                              loading={savingSlug[error.id]}
                                              onClick={() =>
                                                handleUpdateSlug(
                                                  error.id,
                                                  editingSlug[error.id] || error.current
                                                )
                                              }
                                            >
                                              Save
                                            </Button>
                                            <Button
                                              size="small"
                                              onClick={() =>
                                                setEditingSlug(prev => ({
                                                  ...prev,
                                                  [error.id]: error.expected,
                                                }))
                                              }
                                            >
                                              Set to Expected Value
                                            </Button>
                                          </Space>
                                        </div>
                                      </Space>
                                    </Card>
                                  ))}
                                </Space>
                              ),
                              style: { marginBottom: 8, borderColor: '#faad14', borderWidth: 2 },
                            },
                          ]}
                        />
                      )}

                      {result.details.contents && result.details.contents.length > 0 && (
                        <div>
                          <br />
                          <Space style={{ marginBottom: 16 }}>
                            <Text strong>Bulk Re Distribution:</Text>
                            {(() => {
                              // Get serviceAutoDistributions from Service Distribution Check result
                              const serviceDistributionResult = debugResults.find(
                                r => r.step === '3. Service Distribution Check'
                              );
                              const serviceAutoDistributions =
                                serviceDistributionResult?.details?.serviceAutoDistributions || {};

                              return SERVICE_OPTIONS.filter(service => {
                                const serviceAutoDist = serviceAutoDistributions[service.name];
                                return serviceAutoDist?.enabled === true;
                              }).map(service => {
                                const apiServiceName = getApiServiceName(service.name);
                                return (
                                  <Button
                                    key={service.name}
                                    size="small"
                                    type="primary"
                                    loading={reDistributing[`bulk-${service.name}`]}
                                    onClick={() =>
                                      handleBulkReDistributionContents(
                                        Number(seasonId),
                                        result.details.contents,
                                        apiServiceName
                                      )
                                    }
                                  >
                                    Re Distribution All ({service.name})
                                  </Button>
                                );
                              });
                            })()}
                          </Space>
                          <Table
                            dataSource={result.details.contents}
                            rowKey="id"
                            size="small"
                            pagination={{ pageSize: 10 }}
                            columns={[
                              {
                                title: 'ID',
                                dataIndex: 'id',
                                key: 'id',
                                width: 80,
                              },
                              {
                                title: 'Title',
                                dataIndex: 'title',
                                key: 'title',
                              },
                              {
                                title: 'Title EN',
                                dataIndex: 'title_en',
                                key: 'title_en',
                              },
                              {
                                title: 'Slug',
                                dataIndex: 'slug',
                                key: 'slug',
                              },
                              {
                                title: 'Content Type',
                                dataIndex: 'content_type',
                                key: 'content_type',
                              },
                              {
                                title: 'Episode Order',
                                dataIndex: 'episode_order',
                                key: 'episode_order',
                              },
                              {
                                title: 'Episode Part',
                                dataIndex: 'episode_part',
                                key: 'episode_part',
                              },
                              {
                                title: 'Action',
                                key: 'action',
                                width: 120,
                                render: (_: any, record: any) => (
                                  <Button
                                    size="small"
                                    type="primary"
                                    loading={contentDebugLoading[record.id]}
                                    onClick={() => handleDebugContent(record.id)}
                                  >
                                    Debug
                                  </Button>
                                ),
                              },
                            ]}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </Card>
      )}

      {/* Content Debug Results Modal */}
      {Object.entries(contentDebugResults).map(([contentId, result]) => (
        <Modal
          key={contentId}
          title={`Content ${contentId} Debugging Results`}
          open={!!result}
          onCancel={() =>
            setContentDebugResults(prev => {
              const newResults = { ...prev };
              delete newResults[Number(contentId)];
              return newResults;
            })
          }
          width={800}
          footer={[
            <Button
              key="close"
              onClick={() =>
                setContentDebugResults(prev => {
                  const newResults = { ...prev };
                  delete newResults[Number(contentId)];
                  return newResults;
                })
              }
            >
              Close
            </Button>,
          ]}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Video Asset Check */}
            <Card size="small" title="1. Video Asset Check">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Video Asset Connection Status">
                  <Tag color={result.videoAsset.hasVideoAsset ? 'green' : 'red'}>
                    {result.videoAsset.hasVideoAsset ? 'Connected' : 'Not Connected'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total Video Asset Count">
                  {result.videoAsset.totalCount}
                </Descriptions.Item>
                <Descriptions.Item label="Primary Video ID Validation">
                  {result.videoAsset.primaryVideoIdCount === 1 ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      Normal: Primary is True for 1 video_id
                    </Tag>
                  ) : result.videoAsset.primaryVideoIdCount === 0 ? (
                    <Tag color="red" icon={<CloseCircleOutlined />}>
                      Issue: Primary is True for 0 video_ids (should be 1)
                    </Tag>
                  ) : (
                    <Tag color="red" icon={<CloseCircleOutlined />}>
                      Issue: Primary is True for {result.videoAsset.primaryVideoIdCount} video_ids
                      (should be 1)
                    </Tag>
                  )}
                </Descriptions.Item>
                {result.videoAsset.primaryAsset && (
                  <>
                    <Descriptions.Item label="Primary Video ID">
                      {result.videoAsset.primaryAsset.video}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>

            {/* Content Thumbnail & Poster Check */}
            {result.thumbnailAndPoster && (
              <Card size="small" title="1-2. Content Thumbnail & Poster Check">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Default Video Thumbnail">
                    {result.thumbnailAndPoster.hasDefaultVideoThumbnail ? (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        Found
                      </Tag>
                    ) : (
                      <Tag color="red" icon={<CloseCircleOutlined />}>
                        Not Found
                      </Tag>
                    )}
                  </Descriptions.Item>
                  {result.thumbnailAndPoster.contentThumbnailDuplicates.length > 0 ? (
                    <Descriptions.Item label="Content Thumbnail Duplicates">
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {result.thumbnailAndPoster.contentThumbnailDuplicates.map(
                          (dup: any, index: number) => (
                            <Card key={index} size="small" type="inner">
                              <Space direction="vertical" size="small">
                                <div>
                                  <Tag color="orange">
                                    {dup.language} / {dup.orientation} ({dup.count} items)
                                  </Tag>
                                </div>
                                <Space wrap>
                                  {dup.thumbnails.map((thumb: any) => (
                                    <Tag key={thumb.id}>ID: {thumb.id}</Tag>
                                  ))}
                                </Space>
                              </Space>
                            </Card>
                          )
                        )}
                      </Space>
                    </Descriptions.Item>
                  ) : (
                    <Descriptions.Item label="Content Thumbnail Duplicates">
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        Not Found
                      </Tag>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {/* Stream Manifests Check */}
            {result.streamManifests && (
              <Card size="small" title="1-3. Content Stream Manifests Check">
                <Collapse
                  items={Object.entries(result.streamManifests)
                    .filter(([serviceName]) => serviceName && serviceName.trim() !== '')
                    .map(([serviceName, serviceManifests]) => {
                      const manifests = serviceManifests as {
                        count: number;
                        manifests: Array<{
                          type: string;
                          preset: string;
                          status: string;
                          stream_url: string;
                        }>;
                        statusCounts: Record<string, number>;
                      };

                      // Check if all manifests are Complete (case-insensitive)
                      const allComplete = manifests.manifests.every(
                        manifest =>
                          manifest.status?.toLowerCase() === 'complete' ||
                          manifest.status?.toLowerCase() === 'completed'
                      );
                      const statusText = allComplete ? 'complete' : 'failed';
                      const statusColor = allComplete ? 'green' : 'red';

                      const key = `${result.contentId}-${serviceName}`;
                      const isLoading = regeneratingManifests[key] || false;

                      return {
                        key: serviceName,
                        label: (
                          <Space>
                            <span>Service: {serviceName}</span>
                            <Tag color={statusColor}>{statusText}</Tag>
                            <Button
                              size="small"
                              type="primary"
                              loading={isLoading}
                              onClick={e => {
                                e.stopPropagation();
                                handleAddSubtitle(result.contentId, serviceName);
                              }}
                            >
                              Regenerate Stream Manifests
                            </Button>
                          </Space>
                        ),
                        children: (
                          <div>
                            <Descriptions column={1} size="small">
                              <Descriptions.Item label="Stream Manifest Count">
                                {manifests.count}
                              </Descriptions.Item>
                              <Descriptions.Item label="Status Counts">
                                <Space wrap>
                                  {Object.entries(manifests.statusCounts).map(
                                    ([status, count]: [string, any]) => (
                                      <Tag
                                        key={status}
                                        color={
                                          status?.toLowerCase() === 'complete' ||
                                          status?.toLowerCase() === 'completed'
                                            ? 'green'
                                            : 'orange'
                                        }
                                      >
                                        {status}: {count}
                                      </Tag>
                                    )
                                  )}
                                </Space>
                              </Descriptions.Item>
                            </Descriptions>
                            {manifests.manifests.length > 0 && (
                              <Table
                                dataSource={manifests.manifests}
                                rowKey={(record: any, index?: number) =>
                                  `${serviceName}-${record.type}-${index || 0}`
                                }
                                size="small"
                                pagination={false}
                                columns={[
                                  { title: 'Type', dataIndex: 'type', key: 'type' },
                                  { title: 'Preset', dataIndex: 'preset', key: 'preset' },
                                  {
                                    title: 'Status',
                                    dataIndex: 'status',
                                    key: 'status',
                                    render: (status: string) => (
                                      <Tag
                                        color={
                                          status?.toLowerCase() === 'complete' ||
                                          status?.toLowerCase() === 'completed'
                                            ? 'green'
                                            : 'orange'
                                        }
                                      >
                                        {status}
                                      </Tag>
                                    ),
                                  },
                                ]}
                              />
                            )}
                          </div>
                        ),
                      };
                    })}
                />
              </Card>
            )}

            {/* Video Stream Manifests Check */}
            {result.videoStreamManifests && (
              <Card size="small" title="1-4. Video Stream Manifests Check">
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Space>
                    <Button
                      size="small"
                      type="primary"
                      loading={
                        transcodingVideo[result.videoStreamManifests.primaryVideoId] || false
                      }
                      onClick={() =>
                        handleVideoTranscode(result.videoStreamManifests.primaryVideoId)
                      }
                    >
                      Start Video Transcoding
                    </Button>
                  </Space>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Primary Video ID">
                      {result.videoStreamManifests.primaryVideoId}
                    </Descriptions.Item>
                    <Descriptions.Item label="Stream Manifest Count">
                      {result.videoStreamManifests.count}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status Counts">
                      <Space wrap>
                        {Object.entries(result.videoStreamManifests.statusCounts).map(
                          ([status, count]: [string, any]) => (
                            <Tag
                              key={status}
                              color={
                                status?.toLowerCase() === 'complete' ||
                                status?.toLowerCase() === 'completed'
                                  ? 'green'
                                  : 'orange'
                              }
                            >
                              {status}: {count}
                            </Tag>
                          )
                        )}
                      </Space>
                    </Descriptions.Item>
                  </Descriptions>
                  {result.videoStreamManifests.manifests.length > 0 && (
                    <Table
                      dataSource={result.videoStreamManifests.manifests}
                      rowKey={(record: any, index?: number) => `video-${record.type}-${index || 0}`}
                      size="small"
                      pagination={false}
                      columns={[
                        { title: 'Type', dataIndex: 'type', key: 'type' },
                        { title: 'Preset', dataIndex: 'preset', key: 'preset' },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          key: 'status',
                          render: (status: string) => (
                            <Tag
                              color={
                                status?.toLowerCase() === 'complete' ||
                                status?.toLowerCase() === 'completed'
                                  ? 'green'
                                  : 'orange'
                              }
                            >
                              {status}
                            </Tag>
                          ),
                        },
                      ]}
                    />
                  )}
                </Space>
              </Card>
            )}

            {/* Service Distribution Check */}
            {result.distributionStatus && (
              <Card size="small" title="1-5. Service Distribution Check">
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {result.distributionReflection.serviceStatusMessages.length > 0 && (
                    <Alert
                      message={result.distributionReflection.serviceStatusMessages.join(' / ')}
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  {SERVICE_OPTIONS.map(service => {
                    const serviceAutoDist = result.serviceAutoDistributions.distributions.find(
                      (d: any) => {
                        const apiServiceName = getApiServiceName(service.name);
                        return d.service_name === apiServiceName;
                      }
                    );
                    const distributionStatus = result.distributionStatus[service.name];
                    const hasServiceAutoDist = !!serviceAutoDist;
                    const hasDistribution = distributionStatus?.enabled;

                    // Check Distribution Set
                    const serviceDistributionSets = result.distributionSets.sets.filter(
                      (ds: any) =>
                        ds.service_name === service.apiName || ds.service_name === service.name
                    );
                    const hasDistributionSets = serviceDistributionSets.length > 0;
                    return (
                      <Card
                        key={service.name}
                        size="small"
                        title={
                          <Space>
                            <span>{service.name}</span>
                            {hasServiceAutoDist && result.contentId && (
                              <Button
                                size="small"
                                type="primary"
                                loading={reDistributing[service.name]}
                                onClick={() => {
                                  const apiServiceName = getApiServiceName(service.name);
                                  handleReDistributionContent(
                                    result.contentId,
                                    service.name,
                                    apiServiceName
                                  );
                                }}
                              >
                                Re Distribution
                              </Button>
                            )}
                          </Space>
                        }
                        style={{ marginBottom: 16 }}
                      >
                        <Row gutter={16}>
                          <Col span={12}>
                            <Card type="inner" title="Service Auto Distribution" size="small">
                              {hasServiceAutoDist ? (
                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                  <div>
                                    <Text strong style={{ display: 'inline-block', width: 120 }}>
                                      Status:
                                    </Text>
                                    <Tag color="green">Enabled</Tag>
                                  </div>
                                  <div>
                                    <Text strong style={{ display: 'inline-block', width: 120 }}>
                                      Service Name:
                                    </Text>
                                    <Text>{serviceAutoDist.service_name}</Text>
                                  </div>
                                  {serviceAutoDist.subtitle_langs &&
                                    serviceAutoDist.subtitle_langs.length > 0 && (
                                      <div>
                                        <Text
                                          strong
                                          style={{ display: 'inline-block', width: 120 }}
                                        >
                                          Subtitles:
                                        </Text>
                                        <Space wrap>
                                          {serviceAutoDist.subtitle_langs.map((lang: string) => (
                                            <Tag key={lang} color="blue">
                                              {lang}
                                            </Tag>
                                          ))}
                                        </Space>
                                      </div>
                                    )}
                                </Space>
                              ) : (
                                <Tag color="red">Disabled</Tag>
                              )}
                            </Card>
                          </Col>
                          <Col span={12}>
                            <Card type="inner" title="Distribution Status" size="small">
                              {hasDistribution ? (
                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                  <div>
                                    <Text strong style={{ display: 'inline-block', width: 120 }}>
                                      Status:
                                    </Text>
                                    <Tag
                                      color={
                                        distributionStatus.status === 'COMPLETED'
                                          ? 'green'
                                          : distributionStatus.status === 'PENDING'
                                            ? 'orange'
                                            : 'default'
                                      }
                                    >
                                      {distributionStatus.status}
                                    </Tag>
                                  </div>
                                  <div>
                                    <Text strong style={{ display: 'inline-block', width: 150 }}>
                                      Content Consumer:
                                    </Text>
                                    <Text>{distributionStatus.content_consumer_name}</Text>
                                  </div>
                                  <div>
                                    <Text strong style={{ display: 'inline-block', width: 120 }}>
                                      Modified:
                                    </Text>
                                    <Text>{distributionStatus.modified}</Text>
                                  </div>
                                </Space>
                              ) : (
                                <Tag color="red">Disabled</Tag>
                              )}
                            </Card>
                          </Col>
                        </Row>
                        {/* Reflection status display */}
                        {hasDistribution &&
                          distributionStatus.modified &&
                          result.contentData?.modified && (
                            <div style={{ marginTop: 16 }}>
                              {distributionStatus.status !== 'COMPLETED' ||
                              new Date(distributionStatus.modified) <
                                new Date(result.contentData.modified) ? (
                                <Text>
                                  <Tag color="orange" icon={<WarningOutlined />}>
                                    Newly modified content has not been reflected
                                  </Tag>
                                </Text>
                              ) : (
                                <Text>
                                  <Tag color="green" icon={<CheckCircleOutlined />}>
                                    Newly modified content has been reflected
                                  </Tag>
                                </Text>
                              )}
                            </div>
                          )}
                        {/* Distribution Set information */}
                        {hasServiceAutoDist && (
                          <div style={{ marginTop: 16 }}>
                            <Card type="inner" title="Distribution Set" size="small">
                              {hasDistributionSets ? (
                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                  <div>
                                    <Text strong style={{ display: 'inline-block', width: 150 }}>
                                      Distribution Sets:
                                    </Text>
                                    <Space
                                      direction="vertical"
                                      size="small"
                                      style={{ marginTop: 8 }}
                                    >
                                      {serviceDistributionSets.map((ds: any, idx: number) => (
                                        <div key={idx}>
                                          <Space>
                                            <Tag>
                                              {ds.service_name} - Episode ID: {ds.episode_id}
                                            </Tag>
                                            <Button
                                              type="link"
                                              onClick={() =>
                                                handleOpenContentDistributionAdmin(
                                                  ds.episode_id,
                                                  ds.service_name
                                                )
                                              }
                                            >
                                              Link to
                                            </Button>
                                          </Space>
                                        </div>
                                      ))}
                                    </Space>
                                  </div>
                                </Space>
                              ) : (
                                <Tag color="red">No Distribution Set</Tag>
                              )}
                            </Card>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </Space>
              </Card>
            )}

            {/* Validation Results */}
            {result.validationIssues.length > 0 && (
              <Card
                size="small"
                title="Relationship Validation Results"
                style={{ borderColor: '#ff4d4f', borderWidth: 2 }}
              >
                <Alert
                  message="Validation Issues Found"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {result.validationIssues.map((issue: string, index: number) => (
                        <li key={index}>{issue}</li>
                      ))}
                    </ul>
                  }
                  type="error"
                  showIcon
                />
              </Card>
            )}

            {result.validationIssues.length === 0 &&
              result.videoAsset.hasVideoAsset &&
              result.videoAsset.isValidPrimaryCount && (
                <Card size="small" style={{ borderColor: '#52c41a', borderWidth: 2 }}>
                  <Alert message="All validations passed." type="success" showIcon />
                </Card>
              )}
          </Space>
        </Modal>
      ))}
    </div>
  );
};

export default Debug;
